/**
 * SRES · ProjectStageLayout — layout route that wraps every one of the
 * 9 stage pages with the shared 9-step sidebar (Iter82 · Feb 2026).
 *
 * Used in App.js to unify navigation across:
 *   • /app/projects/:pid/setup            (Stage 2)
 *   • /app/projects/:pid/wizard/*         (Stages 3, 4, 6, 7, 8)
 *   • /app/projects/:pid/logs-maps        (Stage 5)
 *   • /app/projects/:pid/reports          (Stage 9)
 *   • /app/projects/:pid/logs, /gis       (legacy stage-5 landings)
 *
 * The sidebar handles the current-stage highlighting itself via
 * `useLocation`, so this layout is a pure structural wrapper.
 */
import { useEffect, useState } from "react";
import { Outlet, useParams, useLocation, useNavigate } from "react-router-dom";
import { SidebarSimple, ArrowsOutSimple, List, X, EyeSlash, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import StagesSidebar from "@/components/StagesSidebar";
import PortfolioBreadcrumb from "@/components/PortfolioBreadcrumb";
import { flowNeighbours, resolveFlowKey } from "@/config/simplified_flow";
import { useCinemaMode } from "@/lib/useCinemaMode";
import CinemaOnboardingCoach from "@/components/CinemaOnboardingCoach";

// Iter85 · Feb 2026 · Per-project sidebar preference. Users typically
// want the rail collapsed during Run Estimation / Cockpit but expanded
// on Setup / Logs. Storing per-pid preserves that muscle memory.
const LS_KEY = (pid) => `sres.sidebar-collapsed:${pid || "_global"}`;
const LS_FALLBACK = "sres.sidebar-collapsed";
// Iter111 · Feb 2026 · Cinema-mode "hidden completely" state, now
// managed by the shared `useCinemaMode` hook so both the top
// BrandHeader and this sidebar layout can toggle it in lock-step.

function readCollapsed(pid) {
  try {
    const per = localStorage.getItem(LS_KEY(pid));
    if (per !== null) return per === "1";
    return localStorage.getItem(LS_FALLBACK) === "1";
  } catch { return false; }
}

export default function ProjectStageLayout() {
  const { pid } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => readCollapsed(pid));
  // Iter110 · Feb 2026 · Mobile drawer state. Independent from the
  // desktop `collapsed` preference — on mobile the sidebar is a
  // full-screen overlay drawer that closes on route change.
  const [mobileOpen, setMobileOpen] = useState(false);
  // Iter112 · Feb 2026 · Cinema-mode state now sourced from the shared
  // `useCinemaMode` hook — the top BrandHeader hides itself in
  // lock-step whenever `hidden === true`.
  const [hidden, setHidden] = useCinemaMode();
  // Close the mobile drawer whenever the pathname changes so tapping
  // a stage link inside the drawer navigates AND dismisses it.
  useEffect(() => { setMobileOpen(false); }, [pathname]);
  // Re-read whenever the project id changes so switching between
  // projects respects each project's own preference.
  useEffect(() => { setCollapsed(readCollapsed(pid)); }, [pid]);
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY(pid), collapsed ? "1" : "0");
      // Keep the global key in sync as a "last-used" default for new
      // projects that don't have a preference yet.
      localStorage.setItem(LS_FALLBACK, collapsed ? "1" : "0");
    } catch { /* silent */ }
  }, [collapsed, pid]);
  // Iter83 · Feb 2026 · Cinema-mode shortcut. ⌘/Ctrl + \ toggles the
  // 9-step rail so the user can reclaim the full width for the 3D
  // viewer, map gallery, or block-model preview.
  //
  // Iter88 · Feb 2026 · ⌘/Ctrl + → / ← walk the 9-stage sequence
  // using flowNeighbours(). Skips inputs / textareas / contenteditable.
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) {
        return;
      }
      // ── Cinema-mode toggle (Iter112) ────────────────────────────────
      //   ⌘/Ctrl + Shift + \  → hide EVERYTHING (top nav + sidebar).
      //   Bound before the plain ⌘ + \ check so Shift wins.
      if (e.shiftKey && (e.key === "\\" || e.code === "Backslash")) {
        e.preventDefault();
        setHidden((v) => !v);
        return;
      }
      // ── Sidebar toggle ─────────────────────────────────────────────
      if (e.key === "\\" || e.code === "Backslash") {
        e.preventDefault();
        setCollapsed((c) => !c);
        return;
      }
      // ── 9-stage keyboard nav ───────────────────────────────────────
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        if (!pid) return;
        // Iter88 · Feb 2026 · don't steal arrow keys while a Radix
        // Dialog / Popover / HoverCard is open — the user is
        // interacting with the overlay, not the underlying page.
        if (document.querySelector('[role="dialog"][data-state="open"]')) return;
        const activeKey = resolveFlowKey(pathname);
        if (!activeKey) return;
        const { prev, next } = flowNeighbours(activeKey);
        const target = e.key === "ArrowRight" ? next : prev;
        if (!target) {
          toast.info(
            e.key === "ArrowRight"
              ? "You're on the last stage"
              : "You're on the first stage"
          );
          e.preventDefault();
          return;
        }
        e.preventDefault();
        navigate(target.url(pid));
        toast.success(`Stage ${target.n} · ${target.label}`);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pid, pathname, navigate, setHidden]);

  return (
    <div className="flex h-full min-h-0 overflow-hidden bg-slate-100"
              data-testid="project-stage-layout"
              data-sidebar-collapsed={collapsed ? "true" : "false"}
              data-sidebar-hidden={hidden ? "true" : "false"}>
      {/* Iter110 · Mobile hamburger — only visible < md AND when sidebar
          is not currently fully hidden (the "Show sidebar" FAB below
          handles the hidden case). Fixed to the top-left of the main
          content so it never overlaps the header. */}
      {!hidden && (
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open workflow sidebar"
          data-testid="sidebar-mobile-open"
          className="md:hidden fixed top-[14px] left-2 z-30 w-9 h-9 rounded-md
                          bg-white border border-slate-200 shadow-sm text-slate-700
                          hover:text-brand-blue hover:border-brand-blue
                          flex items-center justify-center transition">
          <List size={18} weight="bold" />
        </button>
      )}

      {/* Iter111 · Universal "Show sidebar" FAB — visible on ALL breakpoints
          whenever the sidebar is fully hidden. Bottom-left so it doesn't
          collide with the wizard action bar at the bottom-right on
          desktop, and lands above the safe-area on mobile. */}
      {hidden && (
        <button
          type="button"
          onClick={() => { setHidden(false); setMobileOpen(true); }}
          aria-label="Show sidebar"
          data-testid="sidebar-show-fab"
          className="fixed bottom-4 left-4 z-40 h-10 pl-3 pr-4 rounded-full
                          bg-brand-blue text-white text-[12.5px] font-semibold
                          shadow-lg hover:opacity-95 active:scale-[0.97]
                          flex items-center gap-2 transition"
                  style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
          <Eye size={15} weight="bold" />
          Show sidebar
        </button>
      )}

      {/* Iter110 · Mobile backdrop (tap to dismiss) */}
      {mobileOpen && !hidden && (
        <div
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[1px]"
          data-testid="sidebar-mobile-backdrop"
          aria-hidden />
      )}

      {/* Desktop rail / expanded sidebar + mobile drawer.
          When `hidden` is true we bail out entirely so the main pane
          takes 100 % of the viewport width — perfect for 3D viewers,
          maps, and reports on smaller devices. */}
      {!hidden && (collapsed ? (
        // Collapsed rail — a slim 40 px vertical strip with a single
        // Expand button that also renders the keyboard hint.
        // Hidden on mobile — the drawer takes over there.
        <div className="hidden md:flex w-10 shrink-0 h-full bg-white border-r border-slate-200
                             flex-col items-center py-3"
                  data-testid="sidebar-collapsed-rail">
          <button
            type="button"
            title="Expand sidebar (⌘/Ctrl + \)"
            onClick={() => setCollapsed(false)}
            data-testid="sidebar-expand-btn"
            className="w-8 h-8 rounded-md border border-slate-200 flex items-center
                          justify-center text-slate-600 hover:text-brand-blue
                          hover:border-brand-blue transition">
            <ArrowsOutSimple size={16} weight="bold" />
          </button>
          {/* Iter111 · Hide-completely button (rail mode). Frees the last
              40 px of screen when the user needs maximum viewer width. */}
          <button
            type="button"
            title="Hide sidebar completely"
            onClick={() => setHidden(true)}
            data-testid="sidebar-hide-btn-rail"
            className="mt-2 w-8 h-8 rounded-md border border-slate-200 flex items-center
                          justify-center text-slate-500 hover:text-brand-blue
                          hover:border-brand-blue transition">
            <EyeSlash size={14} weight="bold" />
          </button>
          <div className="mt-2 rotate-180 text-[9px] uppercase tracking-widest text-slate-400 font-mono select-none"
                    style={{ writingMode: "vertical-rl" }}>
            ⌘ + \  ·  9-step nav
          </div>
        </div>
      ) : (
        <div className={`relative shrink-0
                              ${mobileOpen
                                  ? "fixed inset-y-0 left-0 z-50 md:static md:z-auto"
                                  : "hidden md:block"}`}
                  data-testid="sidebar-expanded-host">
          <StagesSidebar />
          {/* Desktop control cluster — collapse-to-rail + hide-completely */}
          <div className="hidden md:flex absolute top-2 right-2 items-center gap-1 z-10">
            <button
              type="button"
              title="Hide sidebar completely (frees the whole pane)"
              onClick={() => setHidden(true)}
              data-testid="sidebar-hide-btn"
              className="w-7 h-7 rounded border border-slate-200
                            bg-white text-slate-500 hover:text-brand-blue hover:border-brand-blue
                            flex items-center justify-center transition">
              <EyeSlash size={12} weight="bold" />
            </button>
            <button
              type="button"
              title="Collapse to 40 px rail (⌘/Ctrl + \)"
              onClick={() => setCollapsed(true)}
              data-testid="sidebar-collapse-btn"
              className="w-7 h-7 rounded border border-slate-200
                            bg-white text-slate-500 hover:text-brand-blue hover:border-brand-blue
                            flex items-center justify-center transition">
              <SidebarSimple size={12} weight="bold" />
            </button>
          </div>
          {/* Mobile control cluster — close-drawer + hide-completely */}
          <div className="md:hidden absolute top-2 right-2 flex items-center gap-1 z-10">
            <button
              type="button"
              title="Hide sidebar completely"
              onClick={() => { setHidden(true); setMobileOpen(false); }}
              data-testid="sidebar-mobile-hide"
              className="w-8 h-8 rounded-md border border-slate-200
                            bg-white text-slate-500 hover:text-brand-blue hover:border-brand-blue
                            flex items-center justify-center transition">
              <EyeSlash size={14} weight="bold" />
            </button>
            <button
              type="button"
              title="Close sidebar"
              onClick={() => setMobileOpen(false)}
              data-testid="sidebar-mobile-close"
              className="w-8 h-8 rounded-md border border-slate-200
                            bg-white text-slate-500 hover:text-brand-blue hover:border-brand-blue
                            flex items-center justify-center transition">
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>
      ))}
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden bg-slate-50"
                data-testid="project-stage-body">
        <PortfolioBreadcrumb />
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <Outlet />
        </div>
      </main>
      {/* Iter113 · One-time onboarding coach for cinema mode */}
      <CinemaOnboardingCoach />
    </div>
  );
}
